import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Calendar, HelpCircle } from 'lucide-react';
import { useCMS } from '../context/CMSContext';
import { FAQ_ITEMS, type Lang } from '../data/faq';
import { useSeo, breadcrumb } from '../lib/seo';

const FAQ = () => {
  const { cms, lang: cmsLang } = useCMS();
  const lang: Lang = cmsLang;

  // CMS-driven FAQ content with typed fallback to the bundled dataset,
  // so the visible text and the JSON-LD below always come from ONE array.
  const copy = cms.faq?.copy?.[lang] ?? {
    title: lang === 'en' ? 'Frequently Asked Questions' : lang === 'sc' ? '常见问题' : '常見問題',
    subtitle: '',
    intro: '',
    still: '',
    cta: '',
  };
  const items = cms.faq?.items?.length ? cms.faq.items : FAQ_ITEMS;
  const [open, setOpen] = useState<string | null>(items[0]?.id ?? null);

  const seo = cms.seo.pages.faq;

  useSeo({
    title: copy.title || 'FAQ',
    description: seo.description[lang],
    keywords: seo.keywords[lang],
    path: '/faq',
    jsonLd: [
      breadcrumb([
        { name: lang === 'en' ? 'Home' : '首頁', path: '/' },
        { name: copy.title, path: '/faq' },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        inLanguage: lang === 'tc' ? 'zh-Hant' : lang === 'sc' ? 'zh-Hans' : 'en',
        about: {
          '@type': 'ProfessionalService',
          name: cms.site.orgNameTc,
          alternateName: cms.site.orgNameEn,
        },
        mainEntity: items.map((item) => ({
          '@type': 'Question',
          name: item.q[lang] || item.q.tc || item.q.en,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a[lang] || item.a.tc || item.a.en,
          },
        })),
      },
    ],
  });

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <div className="bg-navy-900 text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold-500/15 text-gold-400 mb-5">
            <HelpCircle className="h-7 w-7" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 font-serif">{copy.title}</h1>
          <p className="text-xl text-gray-300">{copy.subtitle}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <p className="text-lg text-gray-600 leading-relaxed mb-4">{copy.intro}</p>
        {copy.lastUpdatedLabel && cms.lastUpdated && (
          <p className="text-sm text-gray-400 mb-8">
            {copy.lastUpdatedLabel}：{new Date(cms.lastUpdated).toLocaleDateString('zh-HK')}
          </p>
        )}

        {/* Accordion */}
        <div className="divide-y divide-gray-200 border-t border-b border-gray-200">
          {items.map((item) => {
            const isOpen = open === item.id;
            const q = item.q[lang] || item.q.tc || item.q.en;
            const a = item.a[lang] || item.a.tc || item.a.en;
            return (
              <div key={item.id} className="py-2">
                <button
                  onClick={() => setOpen(isOpen ? null : item.id)}
                  className="w-full flex items-start justify-between text-left py-5 gap-4 group"
                  aria-expanded={isOpen}
                >
                  <h2 className="text-lg md:text-xl font-semibold text-navy-900 group-hover:text-gold-600 transition-colors">
                    {q}
                  </h2>
                  <ChevronDown
                    className={`h-6 w-6 flex-shrink-0 text-gold-500 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="text-gray-600 leading-relaxed pb-6 pr-10">{a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-14 bg-gray-50 rounded-xl p-8 text-center border border-gray-100">
          <h3 className="text-2xl font-bold text-navy-900 mb-4 font-serif">{copy.still}</h3>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-6 py-3 bg-navy-900 text-white rounded hover:bg-navy-800 transition-colors"
          >
            <Calendar className="h-5 w-5 mr-2" />
            {copy.cta}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
